import React, { ComponentProps, PropsWithChildren, useRef } from 'react';
import styled, { css } from 'styled-components';
import { useDetectScroll } from '../../hooks/useDetectScroll';
import HBIconButton from '../HBIconButton/HBIconButton';
import { ArrowLeft16, ArrowRight16 } from '@honeybook/icons';
import { inertAttr } from '../../utils/shared.utils';
import { vars } from '../../theme';
import { applyOpacity } from '../../utils/color.utils';

export interface Props {
    className?: string;
    /** Gap in pixels between carousel items. */
    gap: number;
    /**
     * Space to add to start and end when not scrolled
     */
    spaceWhenNotScrolled?: number;
}

const HBCarouselContainer = ({
    className,
    children,
    gap,
    spaceWhenNotScrolled = 0
}: PropsWithChildren<Props>) => {
    const containerRef = useRef<HTMLDivElement>();
    const { isScrolled, isScrolledToEnd } = useDetectScroll({
        elementRef: containerRef,
        direction: 'horizontal',
        spaceWhenNotScrolled
    });

    const scroll = (direction: 'start' | 'end') => {
        const scrollSize = (
            containerRef.current.firstElementChild as HTMLElement
        )?.offsetWidth;
        containerRef.current.scrollBy({
            left: direction === 'start' ? -scrollSize : scrollSize,
            behavior: 'smooth'
        });
    };

    const showStartScrollAvailable = isScrolled;
    const showEndScrollAvailable = !isScrolledToEnd;

    return (
        <Container
            $showStartScrollAvailable={showStartScrollAvailable}
            $showEndScrollAvailable={showEndScrollAvailable}
            $isScrolledToEnd={isScrolledToEnd}
            $spaceWhenNotScrolled={spaceWhenNotScrolled}
        >
            <StyledHBIconButton
                icon={ArrowLeft16}
                label="scroll start button"
                onClick={() => scroll('start')}
                testId="carousel-scroll-start--button"
                inert={!showStartScrollAvailable}
                $isVisible={showStartScrollAvailable}
                {...iconButtonProps}
            />

            <ScrollableContainer
                className={className}
                $gap={gap}
                ref={containerRef}
                $showStartScrollAvailable={showStartScrollAvailable}
                $showEndScrollAvailable={showEndScrollAvailable}
                data-testid="carousel-scrollable-container"
            >
                {children}
            </ScrollableContainer>

            <StyledHBIconButton
                icon={ArrowRight16}
                label="scroll end button"
                onClick={() => scroll('end')}
                testId="carousel-scroll-end--button"
                inert={!showEndScrollAvailable}
                $isVisible={showEndScrollAvailable}
                {...iconButtonProps}
            />
        </Container>
    );
};

export default HBCarouselContainer;

// -------------------------------- Styles -------------------------------- //
const iconButtonProps: Pick<
    ComponentProps<typeof HBIconButton>,
    'buttonTheme' | 'size'
> = {
    buttonTheme: 'neutral-floating',
    size: 'xs'
};

const StyledHBIconButton = styled(HBIconButton).attrs(inertAttr)<{
    $isVisible: boolean;
}>`
    position: absolute;
    z-index: 2;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;
    transition: opacity 0.15s;
    &:first-of-type {
        left: 8px;
    }
    &:last-of-type {
        right: 8px;
    }
    ${({ $isVisible }) =>
        $isVisible &&
        css`
            opacity: 1;
        `}
`;

const Container = styled.div<{
    $showStartScrollAvailable: boolean;
    $showEndScrollAvailable: boolean;
    $spaceWhenNotScrolled: number;
    $isScrolledToEnd: boolean;
}>`
    position: relative;
    width: 100%;
    overflow: hidden;
    transition: padding 0.15s;
    // if not scrolled to start, add padding left
    ${({ $showStartScrollAvailable, $spaceWhenNotScrolled }) =>
        !$showStartScrollAvailable &&
        css`
            padding-left: ${$spaceWhenNotScrolled}px;
        `}

    // if scrolled to end, add padding right
    ${({ $isScrolledToEnd, $spaceWhenNotScrolled }) =>
        $isScrolledToEnd &&
        css`
            padding-right: ${$spaceWhenNotScrolled}px;
        `}
`;

const scrollFadeColor = vars.colors.surface.container;

const ScrollableContainer = styled.div<{
    $gap: number;
    $showStartScrollAvailable: boolean;
    $showEndScrollAvailable: boolean;
}>`
    display: flex;
    gap: ${props => props.$gap}px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;

    > * {
        scroll-snap-align: start;
    }

    &::before,
    &::after {
        content: '';
        position: absolute;
        z-index: 1;
        top: 0;
        bottom: 0;
        width: 16px;
        transition: opacity 0.15s;
    }

    &::before {
        left: 0;
        background: linear-gradient(
            to right,
            ${scrollFadeColor} 0%,
            ${applyOpacity(scrollFadeColor, 0)} 100%
        );
        opacity: ${props => (props.$showStartScrollAvailable ? 1 : 0)};
    }

    &::after {
        right: 0;
        background: linear-gradient(
            to left,
            ${scrollFadeColor} 0%,
            ${applyOpacity(scrollFadeColor, 0)} 100%
        );
        opacity: ${props => (props.$showEndScrollAvailable ? 1 : 0)};
    }
`;
